/**
 * Pure client-side rules for the App Store section of the iPhone card: the same limits, category
 * ids and screenshot sizes as the server, so the form can give instant feedback before a round
 * trip, plus the small parsers/formatters the section's template needs.
 *
 * MIRROR, DON'T DIVERGE: every number, id and message here must equal
 * `olympus-functions/functions/src/apple/listing.ts`. That file is the source of truth — the
 * server is the one that actually enforces these rules; this file only echoes them so the editor
 * doesn't have to round-trip to find out a field is too long. If that file changes, update this
 * one to match.
 */

import { MONTHS } from './dev-editor.util';

/** Same limits as `validateListingInput` in listing.ts. `min`/`max` are character counts. */
export const LIMITS = {
  name: { min: 1, max: 30 },
  subtitle: { min: 0, max: 30 },
  description: { min: 1, max: 4000 },
  keywords: { min: 1, max: 100 },
  promotionalText: { min: 0, max: 170 },
  copyright: { min: 1, max: 200 },
} as const;

/** Same ids as `CATEGORY_IDS` in listing.ts, labelled for a `<select>`. */
export const CATEGORY_LABELS: Record<string, string> = {
  BUSINESS: 'Business',
  PRODUCTIVITY: 'Productivity',
  UTILITIES: 'Utilities',
  EDUCATION: 'Education',
  FINANCE: 'Finance',
  MEDICAL: 'Medical',
  HEALTH_AND_FITNESS: 'Health & Fitness',
  LIFESTYLE: 'Lifestyle',
  NAVIGATION: 'Navigation',
  FOOD_AND_DRINK: 'Food & Drink',
  SHOPPING: 'Shopping',
  TRAVEL: 'Travel',
  NEWS: 'News',
  SOCIAL_NETWORKING: 'Social Networking',
  REFERENCE: 'Reference',
  SPORTS: 'Sports',
  WEATHER: 'Weather',
  ENTERTAINMENT: 'Entertainment',
  PHOTO_AND_VIDEO: 'Photo & Video',
  MUSIC: 'Music',
  BOOKS: 'Books',
  GRAPHICS_AND_DESIGN: 'Graphics & Design',
  DEVELOPER_TOOLS: 'Developer Tools',
};

/** Apple's three accepted portrait screenshot sizes for the 6.9"/6.7" iPhone slot — same as listing.ts. */
export const SCREENSHOT_SIZES: readonly { width: number; height: number }[] = [
  { width: 1320, height: 2868 },
  { width: 1290, height: 2796 },
  { width: 1260, height: 2736 },
];

export function isScreenshotSize(w: number, h: number): boolean {
  return SCREENSHOT_SIZES.some((s) => s.width === w && s.height === h);
}

/** The message the card shows for a screenshot that isn't one of the three accepted sizes. */
export const SCREENSHOT_SIZE_MESSAGE =
  'Screenshots must be portrait PNGs of 1320×2868, 1290×2796 or 1260×2736.';

/**
 * The pixel size of an image file, read entirely client-side. Uses `createImageBitmap`, which
 * doesn't exist in Node — this function is exercised in the browser only and is deliberately not
 * covered by app-store-listing-tests.mjs.
 */
export async function readImageSize(file: File): Promise<{ width: number; height: number }> {
  const bitmap = await createImageBitmap(file);
  try {
    return { width: bitmap.width, height: bitmap.height };
  } finally {
    bitmap.close?.();
  }
}

/** The writable fields of a listing — what the form collects and `saveListing` sends. */
export interface AppStoreListingForm {
  name: string;
  subtitle: string;
  description: string;
  keywords: string;
  supportUrl: string;
  marketingUrl: string;
  promotionalText: string;
  copyright: string;
  primaryCategory: string;
  secondaryCategory: string | null;
  noObjectionableContent: boolean;
  usesThirdPartyContent: boolean;
  /** The builder's word that they have answered, or will answer, Apple's trader status question. */
  traderStatusConfirmed: boolean;
  // Before the two choices:
  // countries: 'ALL' | string[];
  countries: CountryChoice;
  distribution: 'public' | 'unlisted';
}

/** Same values as `CountryChoice` in listing.ts. */
export type CountryChoice = 'WITHOUT_EU' | 'WITH_EU';

/** The card's two choices. Without the EU comes first because it is the default. */
export const COUNTRY_CHOICES: readonly { value: CountryChoice; label: string }[] = [
  { value: 'WITHOUT_EU', label: 'All countries without the EU' },
  { value: 'WITH_EU', label: 'All countries with the EU' },
];

/** A missing value means without the EU, so nothing personal is published unless the builder chose it. */
export function countryChoice(stored: CountryChoice | null | undefined): CountryChoice {
  return stored === 'WITH_EU' ? 'WITH_EU' : 'WITHOUT_EU';
}

export interface AppStoreScreenshot { path: string; width: number; height: number }
export type AppStorePrivacyChoice = 'zavo' | 'own';
// Before the builder could choose their own link:
// export interface AppStorePrivacy { draft?: string; approvedText?: string | null; approvedAt?: string | null; approvedBy?: string | null }
export interface AppStorePrivacy {
  draft?: string; approvedText?: string | null; approvedAt?: string | null; approvedBy?: string | null;
  /** Which policy Apple gets; missing means Zavo's. Choosing one never clears the other's draft or link. */
  choice?: AppStorePrivacyChoice;
  ownUrl?: string;
}
/** What the `privacyChoice` action takes. Zavo's carries no link, so a saved one survives the switch. */
export type AppStorePrivacyChoiceInput = { choice: 'zavo' } | { choice: 'own'; ownUrl: string };
export interface AppStoreWebsiteSteps { appPrivacy?: boolean; agreements?: boolean; unlisted?: boolean }
export type AppStoreChecklistState = 'done' | 'todo' | 'failed';
export interface AppStoreChecklistItem { id: string; label: string; state: AppStoreChecklistState; link?: string; websiteOnly: boolean; message?: string }
export type AppStoreSubmissionStatus = 'submitted' | 'in_review' | 'approved' | 'rejected' | 'failed';
export interface AppStoreSubmission {
  status: AppStoreSubmissionStatus;
  versionId?: string;
  versionString?: string;
  submissionId?: string;
  buildId?: string | null;
  buildNumber?: string;
  publishedVersion?: string | null;
  submittedAt?: string;
  lastState?: string;
  error?: string | null;
}

export interface AppStorePushedOnce { price?: boolean; availability?: boolean }

/** The stored listing doc as the `status` action returns it — every field optional, since it may not exist yet. */
export interface StoredAppStoreListing extends Partial<AppStoreListingForm> {
  screenshots?: AppStoreScreenshot[];
  privacy?: AppStorePrivacy;
  websiteSteps?: AppStoreWebsiteSteps;
  agreementsProblem?: string | null;
  submission?: AppStoreSubmission | null;
  pushedOnce?: AppStorePushedOnce;
  eu?: AppStoreEuCheck | null;
  // Before a check could update it: how many EU territories Apple already had on when the first push found its countries set with the choice without the EU.
  /** How many EU territories were on at the last read that had the choice without the EU: a first push finding its countries set, or a check. A read finding none leaves 0. */
  euOnDespiteChoice?: number;
  /** When that count was last taken from Apple. */
  euOnDespiteChoiceAt?: string;
  /** The version Apple last approved (what iPhones run); kept however many later submissions replace `submission`. */
  approved?: { publishedVersion: string | null; versionString?: string | null; buildId?: string | null; at?: string } | null;
  /** Set while `submit` is in flight, so a second click can be refused before the first call returns. */
  submittingAt?: string | null;
  /** Apple's notes on the rejected version, pasted by the owner (Plan 7). Cleared by every new submission record. */
  rejectionNotes?: AppStoreRejectionNotes | null;
}

/** `listing.rejectionNotes` as the `status` action returns it — the owner's uid is stripped server-side. */
export interface AppStoreRejectionNotes { text: string; versionString?: string | null; at?: string | null }

/** The `status` action's response shape (Task 4; R3 adds `thumbUrls`; fix round 1 adds `reconcileError`). */
export interface AppStoreListingStatus {
  listing: StoredAppStoreListing | null;
  checklist: AppStoreChecklistItem[];
  canSubmit: boolean;
  submission: AppStoreSubmission | null;
  privacyUrl: string | null;
  thumbUrls: string[];
  /** Same meaning as `getIosBuild`'s `reconcileError`: the last background reconcile attempt failed, but the stored state shown is still the last good read. */
  reconcileError: string | null;
  // Before the card could add the EU after the first push:
  //  * availability create, so from here the countries change in App Store Connect, not in the card.
  /**
   * Apple already has this app's countries (`pushedOnce.availability`). Apple refuses a second
   * availability create, so from here the card can add the EU, and can leave it only until a territory
   * has been switched on; after that it's done in App Store Connect.
   */
  countriesLocked: boolean;
  /** Null rather than a blank check, so the card can say none has run instead of implying one found nothing. */
  eu: AppStoreEuCheck | null;
  /** Apple rejected the last version, so no build is submitted automatically until the owner submits by hand (Plan 7). */
  appStoreHeld: boolean;
}

/** Same values as `EuStatus` in listing.ts. */
export type AppStoreEuStatus = 'ready' | 'action_needed' | 'verification_failed';

/** What the last EU check found and did. `status` is null when Apple's answer couldn't settle it. */
export interface AppStoreEuCheck {
  status: AppStoreEuStatus | null;
  on: number | null;
  total: number;
  added: number;
  error: string | null;
  checkedAt?: string;
  /** All 27 have been on at least once, so later checks only read. Sticky: survives a check that failed. */
  allOnOnce?: boolean;
  /** An EU territory has been on at least once, so the card can't take the builder back out. Sticky likewise. */
  anyOnOnce?: boolean;
  /** A check started switching territories on and no reliable read has followed, so Apple may have changed. */
  switchingAt?: number | null;
  // Before a switch-on with no answer counted too: the count comes from Apple's answers to the switch-ons, because the read after them failed.
  /** The count can't be taken for what Apple shows: the read after the switch-ons failed, or one got no answer and Apple may still apply it. */
  unconfirmed?: boolean;
}

/** Zavo declares nothing: the API has no trader path, so the builder has to know it's theirs before they choose. */
// Claimed things Apple's page doesn't say: that the EU waits for the declaration, and that nothing else does.
// export const EU_DECLARATION_NOTICE =
//   "The EU needs a trader declaration, and it's yours to do on Apple's site. If you declare as a trader on an individual account, Apple publicly shows your address, phone and email on your EU product page. Your app won't be available in the EU until it's done. Every other country isn't held up: your first submission goes to them, and the EU countries are added after that.";
// Before only the builder's press added the EU, this could read as happening by itself:
// export const EU_DECLARATION_NOTICE =
//   "Apple asks about your trader status, and its answers include not being a trader. That's your own assessment. A trader's address, phone and email are published on the EU product page (for an organisation, the address on its D-U-N-S record). Your first submission doesn't include the EU; the card adds it when Apple shows nothing outstanding.";
export const EU_DECLARATION_NOTICE =
  "Apple asks about your trader status, and its answers include not being a trader. That's your own assessment. A trader's address, phone and email are published on the EU product page (for an organisation, the address on its D-U-N-S record). Your first submission doesn't include the EU. Zavo adds it only when you press Check my EU status and Apple shows nothing outstanding.";

export const EU_DECLARATION_URL =
  'https://developer.apple.com/help/app-store-connect/manage-compliance-information/manage-european-union-digital-services-act-trader-requirements/';

/** Says "or will" because Apple can ask at submission time, so a builder may not have met the question yet. */
export const TRADER_STATUS_LABEL =
  "I've answered Apple's trader status question in App Store Connect, or will before submitting.";
export const TRADER_STATUS_HINT =
  "Apple asks this even for apps not sold in the EU. The answer is yours: Zavo doesn't declare it.";
/** Same message as listing.ts's TRADER_STATUS_INPUT_MESSAGE. */
const TRADER_STATUS_INPUT_MESSAGE = 'The trader status confirmation must be ticked or unticked.';

/** A change to the EU reaches Apple only through the re-check, so the card has to say so. */
export const EU_SAVE_THEN_CHECK_NOTE = 'Save, then check your EU status here.';

/** Before any check has run there's no status to show, only what the builder has to do for the EU to be added. */
export const EU_NOT_ADDED_LABEL = "Zavo hasn't added the EU yet";
export const EU_NOT_ADDED_DETAIL = "Apple asks you to answer its trader status question on its site. Once you've answered, press Check my EU status.";

export interface EuStatusView { label: string; tone: 'ok' | 'warn' | 'bad' | 'none'; detail: string | null; error: string | null; checked: string | null }

/** The press only adds on the EU, and once all 27 have been on it only reads, so it's named only where it can do something. */
function euCountLine(eu: AppStoreEuCheck, leftEu: boolean): { label: string; detail: string | null } {
  const pressAdds = !leftEu && !eu.allOnOnce;
  if (eu.on === 0) {
    return { label: 'No EU countries were on at the last check.', detail: pressAdds ? "Press Check my EU status to add them, if nothing's outstanding on Apple's site." : null };
  }
  if (pressAdds && eu.on !== null && eu.on < eu.total) {
    return { label: `${eu.on} of ${eu.total} EU countries are on.`, detail: "Press Check my EU status to add the rest, if nothing's outstanding on Apple's site." };
  }
  // Apple's countries can be set before Zavo's first push, so all of them can be on with no status read yet.
  if (!leftEu && eu.on !== null && eu.on === eu.total) {
    return { label: `${eu.on} of ${eu.total} EU countries are on.`, detail: 'Press Check my EU status to see what Apple reports.' };
  }
  return { label: `${eu.on} of ${eu.total} EU countries are on. Change this in App Store Connect.`, detail: null };
}

/** What the card shows for the last EU check. A check that couldn't settle the status never reads as ready. */
export function euStatusView(eu: AppStoreEuCheck | null | undefined, o: { leftEu?: boolean } = {}): EuStatusView {
  // Having left the EU, the declaration isn't what the builder is here for: only how many are still on.
  if (!eu && o.leftEu) return { label: 'Not checked yet', tone: 'none', detail: null, error: null, checked: null };
  // Before a first submission stopped adding the EU by itself, when nothing was left to explain:
  // if (!eu) return { label: 'Not checked yet', tone: 'none', detail: "Check once you've finished the declaration on Apple's site.", error: null, checked: null };
  // Says Zavo hasn't added it rather than that it's out: Apple's countries may have been set before Zavo's first push.
  if (!eu) return { label: EU_NOT_ADDED_LABEL, tone: 'none', detail: EU_NOT_ADDED_DETAIL, error: null, checked: null };
  const error = eu.error ?? null;
  const at = eu.checkedAt ? new Date(eu.checkedAt) : null;
  const checked = at && !isNaN(at.getTime()) ? `Checked ${at.getUTCDate()} ${MONTHS[at.getUTCMonth()]}` : null;
  // Green is only for all of them. Anything less with nothing wrong is the builder's own doing at Apple, so it isn't a failure either.
  // Before it could read "0 of 27", or send a builder to App Store Connect when the press could add the rest:
  // const neutral: EuStatusView = { label: `${eu.on} of ${eu.total} EU countries are on. Change this in App Store Connect.`, tone: 'none', detail: null, error, checked };
  const neutral: EuStatusView = { ...euCountLine(eu, !!o.leftEu), tone: 'none', error, checked };
  if (o.leftEu && eu.on !== null) return neutral;
  if (eu.status === 'verification_failed') {
    return { label: 'Verification failed', tone: 'bad', detail: "Apple couldn't verify your declaration. Correct it on Apple's site, then check again.", error, checked };
  }
  if (eu.status === 'action_needed') {
    // Before it could read as adding them by itself:
    // return { label: "Action needed on Apple's site", tone: 'warn', detail: 'Finish the declaration on Apple\'s site, then check again. The EU countries are added once nothing is outstanding.', error, checked };
    return { label: "Action needed on Apple's site", tone: 'warn', detail: "Finish the declaration on Apple's site, then press Check my EU status again. No EU countries are added while anything is outstanding.", error, checked };
  }
  // Before "EU ready" needed all of them on:
  // if (eu.status === 'ready') {
  if (eu.status === 'ready' && eu.on !== null && eu.on !== eu.total) return neutral;
  if (eu.status === 'ready' && eu.total === 0) return neutral;
  if (eu.status === null && !error && eu.on !== null) return neutral;
  if (eu.status === 'ready') {
    const added = eu.added > 0 ? ` This check added ${eu.added}.` : '';
    return { label: 'EU ready', tone: 'ok', detail: `${eu.on} of ${eu.total} EU countries are on.${added}`, error, checked };
  }
  // A check that stopped partway still knows how many are on, and "couldn't check" alone would read as none.
  const detail = eu.on === null ? null : `${eu.on} of ${eu.total} EU countries are on.`;
  return { label: "Couldn't check your EU status", tone: 'bad', detail, error, checked };
}

// Before a mistaken choice could be undone while nothing had been switched on:
// export function leavingEuLocked(saved: CountryChoice | null | undefined, locked: boolean): boolean {
//   return locked && saved === 'WITH_EU';
// }
/** A territory Zavo switched on can't be switched off from here, so from then on leaving is done in App Store Connect. Same rule as `countriesChangeRefusal` in listing.ts. */
export function leavingEuLocked(saved: CountryChoice | null | undefined, locked: boolean, eu: AppStoreEuCheck | null | undefined): boolean {
  // Before a crash after Apple's change could leave the way out open:
  // return locked && saved === 'WITH_EU' && !!eu?.anyOnOnce;
  const mayBeOn = !!eu?.anyOnOnce || (eu?.switchingAt !== null && eu?.switchingAt !== undefined);
  return locked && saved === 'WITH_EU' && mayBeOn;
}

/** After the first push the change to the EU stays open, because the EU is added one territory at a time. */
export function countryOptionLocked(option: CountryChoice, saved: CountryChoice | null | undefined, locked: boolean, eu: AppStoreEuCheck | null | undefined): boolean {
  return option !== 'WITH_EU' && leavingEuLocked(saved, locked, eu);
}

/** The re-check acts on the saved choice, so a radio that differs from it has to be saved first. */
export const EU_CHECK_SAVE_FIRST = 'Save your countries choice first.';

/** With the choice saved as without the EU the status is only about what Apple may have on, so it shows only if some may be. Same rule as the server's `euCheck`. */
export function euLeftEuStatusShown(
  listing: Pick<StoredAppStoreListing, 'countries' | 'euOnDespiteChoice'> | null | undefined, eu: AppStoreEuCheck | null | undefined, locked: boolean
): boolean {
  if (!listing || !locked || listing.countries === 'WITH_EU') return false;
  return (listing.euOnDespiteChoice ?? 0) > 0 || !!eu?.anyOnOnce;
}

/** A check here only reads, so with a reliable read of none there's nothing it's expected to change. The server would still answer one. */
export function euReadOnlyCheckOffered(
  listing: Pick<StoredAppStoreListing, 'countries' | 'euOnDespiteChoice'> | null | undefined, eu: AppStoreEuCheck | null | undefined, locked: boolean
): boolean {
  // Before a reliable read finding none took the button away:
  // if (!listing || !locked || listing.countries === 'WITH_EU') return false;
  // return (listing.euOnDespiteChoice ?? 0) > 0 || !!eu?.anyOnOnce;
  return euLeftEuStatusShown(listing, eu, locked) && !(eu?.on === 0 && !eu.unconfirmed);
}

/** Doesn't block anything: Apple's countries were set before Zavo's first push, and only the builder can change them there. */
export function euOnDespiteChoiceWarning(listing: Pick<StoredAppStoreListing, 'countries' | 'euOnDespiteChoice' | 'euOnDespiteChoiceAt'> | null | undefined): string | null {
  const n = listing?.euOnDespiteChoice ?? 0;
  if (n <= 0 || countryChoice(listing?.countries) === 'WITH_EU') return null;
  const on = n === 1 ? '1 EU country is on' : `${n} EU countries are on`;
  // Before it said when: the count can be old, and the builder may have changed it at Apple since.
  // return `${on} at Apple for this app, though your choice here is without the EU. To change that, use App Store Connect → Pricing and Availability.`;
  const at = listing?.euOnDespiteChoiceAt ? new Date(listing.euOnDespiteChoiceAt) : null;
  const when = at && !isNaN(at.getTime()) ? ` (${at.getUTCDate()} ${MONTHS[at.getUTCMonth()]})` : '';
  return `As of the last check${when}, ${on} at Apple for this app, though your choice here is without the EU. To change that, use App Store Connect → Pricing and Availability.`;
}

/** What the card says under the locked Countries controls. */
export const COUNTRIES_LOCKED_MESSAGE =
  "Apple already has this app's countries. To change them, use App Store Connect → Pricing and Availability.";

/** App Store Connect's Pricing and Availability page for this app. */
export function countriesPricingUrl(appleAppId: string | null | undefined): string {
  return `https://appstoreconnect.apple.com/apps/${appleAppId ?? ''}/distribution/pricing`;
}

/** The age-rating checkbox says exactly what ticking it declares to Apple (setNoObjectionableAgeRating). */
export const AGE_RATING_LABEL =
  'My app has none of these: violence or weapons, gambling, contests or loot boxes, adult or mature content, horror, crude language, alcohol, tobacco or drug references, medical or health topics, user-generated content, chat or messaging, social media, open web browsing, or ads.';
export const AGE_RATING_HINT =
  "Apple's age rating is set from this. Apps with any of these need the full questionnaire, which this card doesn't cover yet.";

/** Shown beside Save once a submission exists and didn't fail: a save doesn't reach Apple by itself. */
export const NEXT_SUBMISSION_NOTE = 'Changes here reach Apple with the next submission.';

export function editsWaitForNextSubmission(submission: { status?: string } | null | undefined): boolean {
  return !!submission && submission.status !== 'failed';
}

function isHttpsUrl(s: string): boolean {
  if (!s) return false;
  try {
    return new URL(s).protocol === 'https:';
  } catch {
    return false;
  }
}

/** Same message as listing.ts's PRIVACY_LINK_MESSAGE. */
export const PRIVACY_LINK_MESSAGE = 'Privacy policy link must be a valid https:// URL.';

const OWN_PRIVACY_LINK_MAX = 2048;

/**
 * The builder's own privacy link as the server stores it, or null when it isn't one. Stricter than the
 * URL parser, which repairs `https:acme.example` and strips tabs and newlines without a word.
 * Same rule as `ownPrivacyLink` in listing.ts.
 */
export function ownPrivacyLink(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const link = raw.trim();
  if (link.length > OWN_PRIVACY_LINK_MAX || /\s/.test(link) || !/^https:\/\/[^/]/i.test(link)) return null;
  let url: URL;
  try { url = new URL(link); } catch { return null; }
  if (url.protocol !== 'https:' || url.username || url.password) return null;
  // The parser decodes a host and rewrites any spelling of an address, so these are read from the raw link and the result.
  if (/^https:\/\/[^/?#]*%/i.test(link)) return null;
  if (url.hostname.startsWith('[') || /^\d+(\.\d+){3}$/.test(url.hostname)) return null;
  if (!/^[^.]+(\.[^.]+)+$/.test(url.hostname)) return null;
  return url.href.length > OWN_PRIVACY_LINK_MAX ? null : url.href;
}

export function privacyLinkError(link: string | null | undefined): string | null {
  // Before own links had their own rule; support and marketing links still use this one:
  // return isHttpsUrl((link ?? '').trim()) ? null : PRIVACY_LINK_MESSAGE;
  return ownPrivacyLink(link) ? null : PRIVACY_LINK_MESSAGE;
}

export function privacyChoiceOf(listing: { privacy?: AppStorePrivacy } | null | undefined): AppStorePrivacyChoice {
  return listing?.privacy?.choice === 'own' ? 'own' : 'zavo';
}

/** Whether the card fills in Zavo's draft: only with Zavo's policy chosen and nothing written yet. */
export function shouldDraftPrivacy(listing: { privacy?: AppStorePrivacy } | null | undefined): boolean {
  const p = listing?.privacy;
  return privacyChoiceOf(listing) === 'zavo' && !p?.approvedText && !p?.draft;
}

/**
 * What picking a radio has to tell the server, or null for nothing. An own link with none saved yet
 * sends nothing, because the server refuses that choice until there is a link to go with it.
 */
export function privacyChoiceToSend(
  target: AppStorePrivacyChoice, listing: { privacy?: AppStorePrivacy } | null | undefined
): AppStorePrivacyChoiceInput | null {
  if (target === privacyChoiceOf(listing)) return null;
  if (target === 'zavo') return { choice: 'zavo' };
  const ownUrl = listing?.privacy?.ownUrl;
  return ownUrl ? { choice: 'own', ownUrl } : null;
}

/** Same message as listing.ts's AGE_RATING_MESSAGE. */
const AGE_RATING_MESSAGE =
  "Apps with age-rated content need Apple's age-rating questionnaire, which this card doesn't cover yet.";

/**
 * Client-side echo of `validateListingInput` in listing.ts, for instant feedback before Save is
 * pressed. Returns every error found (not just the first), each in the same words the server
 * uses, so the card can list them all inline. An empty array means the server-side call is
 * expected to succeed — the server remains the actual authority.
 */
export function clientListingErrors(form: Partial<AppStoreListingForm>): string[] {
  const errors: string[] = [];

  const name = (form.name ?? '').trim();
  if (name.length < LIMITS.name.min || name.length > LIMITS.name.max) {
    errors.push('App name must be between 1 and 30 characters.');
  }

  const subtitle = (form.subtitle ?? '').trim();
  if (subtitle.length > LIMITS.subtitle.max) {
    errors.push('Subtitle must be 30 characters or fewer.');
  }

  const description = (form.description ?? '').trim();
  if (description.length < LIMITS.description.min || description.length > LIMITS.description.max) {
    errors.push('Description must be between 1 and 4000 characters.');
  }

  const keywords = (form.keywords ?? '').trim();
  if (keywords.length < LIMITS.keywords.min || keywords.length > LIMITS.keywords.max) {
    errors.push('Keywords must be between 1 and 100 characters.');
  }

  const supportUrl = (form.supportUrl ?? '').trim();
  if (!isHttpsUrl(supportUrl)) {
    errors.push('Support URL must be a valid https:// URL.');
  }

  const marketingUrl = (form.marketingUrl ?? '').trim();
  if (marketingUrl !== '' && !isHttpsUrl(marketingUrl)) {
    errors.push('Marketing URL must be empty or a valid https:// URL.');
  }

  const promotionalText = (form.promotionalText ?? '').trim();
  if (promotionalText.length > LIMITS.promotionalText.max) {
    errors.push('Promotional text must be 170 characters or fewer.');
  }

  const copyright = (form.copyright ?? '').trim();
  if (copyright.length < LIMITS.copyright.min || copyright.length > LIMITS.copyright.max) {
    errors.push('Copyright must be between 1 and 200 characters.');
  }

  const primaryCategory = (form.primaryCategory ?? '').trim();
  if (!(primaryCategory in CATEGORY_LABELS)) {
    errors.push("Primary category must be one of Apple's app categories.");
  }

  const secondaryCategory = form.secondaryCategory === null || form.secondaryCategory === undefined
    ? null : String(form.secondaryCategory).trim();
  if (secondaryCategory !== null) {
    if (!(secondaryCategory in CATEGORY_LABELS)) {
      errors.push("Secondary category must be one of Apple's app categories.");
    } else if (secondaryCategory === primaryCategory) {
      errors.push('Secondary category must be different from the primary category.');
    }
  }

  const countries = form.countries;
  // "Every country" and the typed list are no longer choices.
  // if (!countriesOk) {
  //   errors.push('Countries must be "ALL" or a non-empty list of 3-letter country codes.');
  // }
  // const countriesOk = countries === 'ALL'
  //   || (Array.isArray(countries) && countries.length > 0 && countries.every((c) => typeof c === 'string' && /^[A-Z]{3}$/.test(c)));
  // A missing value means without the EU, as on the server.
  const countriesOk = countries === undefined || countries === null || COUNTRY_CHOICES.some((c) => c.value === countries);
  if (!countriesOk) {
    errors.push('Countries must be All countries without the EU or All countries with the EU.');
  }

  if (form.distribution !== 'public' && form.distribution !== 'unlisted') {
    errors.push('Distribution must be "public" or "unlisted".');
  }

  if (form.noObjectionableContent !== true) {
    errors.push(AGE_RATING_MESSAGE);
  }

  // Unticked is no error: the listing still saves, and the checklist step is what holds a submission back.
  const trader = form.traderStatusConfirmed;
  if (trader !== undefined && typeof trader !== 'boolean') {
    errors.push(TRADER_STATUS_INPUT_MESSAGE);
  }

  return errors;
}

const SUBMISSION_LABELS: Record<AppStoreSubmissionStatus, string> = {
  submitted: 'Submitted — waiting for Apple',
  in_review: "In Apple's review",
  approved: 'Approved — live on the App Store',
  rejected: 'Apple rejected this version',
  failed: "Couldn't submit",
};

/** The status line's label. `''` for no submission yet (null/undefined). */
export function submissionLabel(status: AppStoreSubmissionStatus | string | null | undefined): string {
  if (!status) return '';
  return SUBMISSION_LABELS[status as AppStoreSubmissionStatus] ?? '';
}

// The card no longer has a typed country list, so nothing parses one.
// /**
//  * Parses the "Only these" countries text field into `"ALL"`, a non-empty list of 3-letter codes,
//  * or `null` when the text doesn't parse as either (empty, or contains something that isn't a
//  * 3-letter code). Codes may be separated by commas, whitespace, or both, are upper-cased, and are
//  * de-duplicated keeping the order of first occurrence (so "USA, usa ,USA" → `['USA']`).
//  */
// export function parseCountries(text: string): 'ALL' | string[] | null {
//   const trimmed = (text ?? '').trim();
//   if (trimmed === '') return null;
//   if (trimmed.toUpperCase() === 'ALL') return 'ALL';
//
//   const codes = trimmed
//     .split(/[\s,]+/)
//     .map((c) => c.trim().toUpperCase())
//     .filter((c) => c.length > 0);
//
//   if (codes.length === 0) return null;
//   if (!codes.every((c) => /^[A-Z]{3}$/.test(c))) return null;
//
//   const seen = new Set<string>();
//   const deduped: string[] = [];
//   for (const c of codes) {
//     if (!seen.has(c)) { seen.add(c); deduped.push(c); }
//   }
//   return deduped;
// }

// ---- Plan 7: after Apple rejects a version ----
// Apple's API carries no rejection reason, so the owner pastes Apple's notes into the card. Same
// limit as the server's `rejectionNotes` action (appStoreListing): trimmed, 0–4000 characters,
// empty clears them.

/** The rejected block's copy, split so the template can set the button's name in bold. */
export const REJECTED_PAUSE = {
  before: "Apple rejected this version. Automatic App Store submission is paused — read Apple's notes in App Store Connect, fix the problem, then press ",
  button: 'Submit to App Store',
  after: '.',
} as const;

/** Mirrors the server's appStoreRejectionHold: a rejected submission pauses automatic submission. */
export function rejectedPauseShown(submission: { status?: string } | null | undefined): boolean {
  return submission?.status === 'rejected';
}

export const REJECTION_NOTES_LABEL = "Apple's notes (paste them here so your team can see why)";
export const REJECTION_NOTES_MAX = 4000;

/** What Save sends: the trimmed text, or why it can't be sent. Empty text is valid — it clears the notes. */
export function rejectionNotesInput(text: string | null | undefined): { ok: true; text: string } | { ok: false; error: string } {
  const t = (text ?? '').trim();
  if (t.length > REJECTION_NOTES_MAX) return { ok: false, error: `Apple's notes must be ${REJECTION_NOTES_MAX} characters or fewer.` };
  return { ok: true, text: t };
}

/** The saved notes, or null when none were saved (editors then see nothing). */
export function savedRejectionNotes(listing: { rejectionNotes?: AppStoreRejectionNotes | null } | null | undefined): AppStoreRejectionNotes | null {
  const n = listing?.rejectionNotes;
  return n && typeof n.text === 'string' && n.text.trim() ? n : null;
}

/** The heading over the saved notes, with the day they were saved (formatted like the card's other dates). */
export function rejectionNotesHeading(notes: { at?: string | null } | null | undefined): string {
  const d = notes?.at ? new Date(notes.at) : null;
  return d && !isNaN(d.getTime()) ? `Apple's notes · saved ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}` : "Apple's notes";
}
